import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import type { SlotRegistrationStatus } from "../../../views/scheduling/schedulingApi";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  currentStatus: SlotRegistrationStatus | null | undefined;
  freelancerName?: string | null;
  storeName?: string | null;
  day?: string | null;
  onConfirm: (nextStatus: SlotRegistrationStatus) => Promise<void> | void;
};

const STATUS_OPTIONS: Array<{
  value: SlotRegistrationStatus;
  title: string;
  description: string;
}> = [
  {
    value: "PENDENTE",
    title: "PENDENTE",
    description: "Presença ainda não confirmada",
  },
  {
    value: "APROVADO",
    title: "APROVADO",
    description: "Freelancer compareceu e foi aprovado",
  },
  {
    value: "AUSENTE",
    title: "AUSENTE",
    description: "Freelancer não compareceu",
  },
  {
    value: "CANCELADO",
    title: "CANCELADO",
    description: "Inscrição cancelada",
  },
];

function safeStatusLabel(s?: string | null) {
  return (s ?? "").trim().toUpperCase();
}

function normalizeStatus(s?: string | null): SlotRegistrationStatus {
  const up = safeStatusLabel(s);
  if (up === "APROVADO") return "APROVADO";
  if (up === "AUSENTE") return "AUSENTE";
  if (up === "CANCELADO") return "CANCELADO";
  return "PENDENTE";
}

function formatBrDate(value?: string | null) {
  if (!value) return "-";
  const iso = value.slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
}

const RegistrationStatusModal: React.FC<Props> = ({
  isOpen,
  onClose,
  currentStatus,
  freelancerName,
  storeName,
  day,
  onConfirm,
}) => {
  const normalizedCurrentStatus = useMemo(
    () => normalizeStatus(currentStatus ?? null),
    [currentStatus]
  );

  const [selectedStatus, setSelectedStatus] = useState<SlotRegistrationStatus>(normalizedCurrentStatus);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedStatus(normalizedCurrentStatus);
  }, [isOpen, normalizedCurrentStatus]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, saving]);

  const handleOverlayClick = () => {
    if (!saving) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const handleConfirm = async () => {
    if (!selectedStatus) {
      toast.info("Selecione um status.");
      return;
    }

    if (selectedStatus === normalizedCurrentStatus) {
      toast.info("Nenhuma alteração para salvar.");
      return;
    }

    try {
      setSaving(true);
      await onConfirm(selectedStatus);
      toast.success("Status atualizado com sucesso.");
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao atualizar status.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">Alterar status</h2>

        <div className="ModalBody">
          <div className="SelectedInfoBox">
            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Freelancer</span>
              <span className="SelectedInfoValue">{freelancerName ?? "-"}</span>
            </div>

            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Loja</span>
              <span className="SelectedInfoValue">{storeName ?? "-"}</span>
            </div>

            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Data</span>
              <span className="SelectedInfoValue">{formatBrDate(day)}</span>
            </div>

            <div className="SelectedInfoRow">
              <span className="SelectedInfoLabel">Status</span>
              <span className="SelectedInfoValue">{normalizedCurrentStatus}</span>
            </div>
          </div>

          <div className="Box">
            <div className="BoxTitle">Selecione o novo status</div>

            <div className="DraftList">
              {STATUS_OPTIONS.map((option) => {
                const selected = selectedStatus === option.value;
                const isCurrent = normalizedCurrentStatus === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`DraftItem DraftItemButton ${selected ? "PresenceDirtyRow" : ""}`}
                    onClick={() => setSelectedStatus(option.value)}
                    disabled={saving}
                    title={option.title}
                  >
                    <div className="DraftInfo">
                      <div className="DraftTitle">{option.title}</div>
                      <div className="DraftSub">{option.description}</div>
                    </div>

                    <div className="SlotBadges">
                      {isCurrent && <span className="SlotBadge">Atual</span>}
                      {selected && <span className="SlotBadge SlotBadgeAccent">Selecionado</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ModalActions">
            <button
              type="button"
              className="ModalButton ModalButtonCancel"
              onClick={onClose}
              disabled={saving}
            >
              Fechar
            </button>

            <button
              type="button"
              className="ModalButton ModalButtonPrimary"
              onClick={handleConfirm}
              disabled={saving || selectedStatus === normalizedCurrentStatus}
              title={
                saving
                  ? "Salvando..."
                  : selectedStatus === normalizedCurrentStatus
                  ? "Nenhuma alteração para salvar"
                  : "Confirmar alteração"
              }
            >
              {saving ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationStatusModal;
